import { Fragment, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Chip,
  List,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import styled from "@emotion/styled";
import { SimpleLensView } from "generated/graphql";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { fetchClientLens, selectClientLenses } from "redux/slices/client";
import { Link } from "react-router-dom";
import { selectIsSupport } from "redux/slices/session";

interface FieldHeaderProps {
  text: string;
}

const FieldHeader = (props: FieldHeaderProps) => {
  const { text } = props;
  return (
    <Typography variant="caption" color="textSecondary">
      <b>{text}</b>
    </Typography>
  );
};

interface StatProps {
  label: string;
  value: string;
}

export const Stat = (props: StatProps) => {
  const { label, value } = props;
  return (
    <Fragment>
      <Typography variant="h6light" style={{ textAlign: "center" }}>
        {value}
      </Typography>
      <FieldHeader text={label} />
    </Fragment>
  );
};

interface LensCardProps {
  lens: SimpleLensView;
  isLensEditor?: boolean;
  click: () => void;
}

const Statistic = styled("span")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
});

export const LensCard = ({ lens, isLensEditor, click }: LensCardProps) => {
  const clientsLenses = useAppSelector(selectClientLenses);
  const clientLens = clientsLenses[lens.id];
  const dispatch = useAppDispatch();
  const isSupport = useAppSelector(selectIsSupport);

  useEffect(() => {
    dispatch(fetchClientLens({ lensId: lens.id }));
  }, [lens.id]);

  return (
    <Card>
      <CardActionArea onClick={click}>
        <CardContent sx={{ display: "grid", gridTemplateColumns: { md: "30% 1fr" }, paddingBottom: 0 }}>
          {isLensEditor && <p className="text-xs text-gray-400 col-span-2">Version {clientLens?.lens.version}</p>}
          <Box
            sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", flexDirection: "row", gap: 1 }}>
            <p className="text-2xl font-bold">{lens.name}</p>
            {lens.hidden && (
              <Tooltip title="Lens only visible in the editor">
                <Chip label="Hidden" />
              </Tooltip>
            )}
          </Box>
          {!clientLens ? (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} sx={{ width: "100px", height: 60 }} />
              ))}
            </Box>
          ) : (
            <List
              sx={{
                display: "flex",
                justifyContent: "space-between",
                pr: { xs: 0, md: 3 },
                mt: { xs: 2, sm: 0 },
              }}>
              <Statistic>
                <Stat label="Total Cases" value={clientLens.caseCount.toLocaleString()} />
              </Statistic>
              <Statistic>
                <Stat label="Active" value={clientLens.casesOverview.activeCount.toLocaleString()} />
              </Statistic>
              <Statistic>
                <Stat label="Inactive" value={clientLens.casesOverview.inactiveCount.toLocaleString()} />
              </Statistic>
              <Statistic>
                <Stat label="Documents" value={clientLens.documentsProcessed.toLocaleString()} />
              </Statistic>
              <Statistic>
                <Stat label="Pages" value={clientLens.pagesProcessed.toLocaleString()} />
              </Statistic>
            </List>
          )}
        </CardContent>
      </CardActionArea>
      <CardActions>
        <Button color="primary" onClick={click}>
          View
        </Button>
        {!isLensEditor && isSupport && (
          <Link
            className="uppercase text-gray-300 hover:text-gray-900 cursor-pointer font-bold text-sm"
            to={`/lens-editor/${lens.id}`}>
            Lens Editor
          </Link>
        )}
      </CardActions>
    </Card>
  );
};

export default LensCard;
